import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Briefcase, Plane, ShieldCheck, ArrowRight, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import OfferCard from "@/components/OfferCard";
import { Button } from "@/components/ui/button";

const VISA_SERVICES = [
  { key: "canada_etudes", icon: GraduationCap, title: "Canada - Études", desc: "Permis d'études, admission et preuve de fonds." },
  { key: "canada_travail", icon: Briefcase, title: "Canada - Travail", desc: "Offre d'emploi et dossier professionnel." },
  { key: "france_etudes", icon: GraduationCap, title: "France - Études", desc: "Attestation d'admission et ressources." },
  { key: "france_travail", icon: Briefcase, title: "France - Travail", desc: "Contrat de travail et justificatifs." },
];

export default function Home() {
  const [offers, setOffers] = useState([]);
  const [whatsapp, setWhatsapp] = useState("");

  useEffect(() => {
    api.get("/offers").then(({ data }) => setOffers(data.slice(0, 3))).catch(() => {});
    api.get("/config").then(({ data }) => setWhatsapp(data.whatsapp_number)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[82vh] flex items-end">
        <img
          src="https://images.unsplash.com/photo-1566622246615-6dbe9c71629a"
          alt="Voyage au coucher du soleil"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-6xl mx-auto px-4 pb-16 pt-24 text-white">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-block bg-accent text-accent-foreground text-xs font-bold px-4 py-1.5 rounded-full mb-5">
              Séjours · Hôtels · Visa & Passeport
            </span>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-shadow-hero">
              Votre prochain voyage commence ici
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/90 max-w-xl text-shadow-hero">
              Réservez vos séjours et hôtels en toute simplicité, contactez notre équipe pour le paiement, et laissez-nous vous accompagner jusqu'à l'obtention de votre visa.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/offers">
                <Button className="rounded-full h-12 px-7 bg-accent text-accent-foreground hover:bg-accent/90 text-base font-bold" data-testid="hero-offers-btn">
                  Voir les offres <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/visa">
                <Button variant="outline" className="rounded-full h-12 px-7 bg-white/10 border-white/40 text-white hover:bg-white/20 text-base font-bold backdrop-blur" data-testid="hero-visa-btn">
                  Accompagnement visa
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured offers */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-head text-3xl font-extrabold text-primary">Offres en vedette</h2>
            <p className="text-muted-foreground mt-1">Nos destinations les plus appréciées</p>
          </div>
          <Link to="/offers" className="hidden sm:inline-flex items-center gap-1 text-accent font-semibold hover:gap-2 transition-all">
            Tout voir <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {offers.map((o) => (
            <OfferCard key={o.id} offer={o} />
          ))}
        </div>
      </section>

      {/* Visa services */}
      <section className="bg-primary text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-2 text-accent font-semibold mb-2">
            <ShieldCheck className="h-5 w-5" /> Visa & Passeport
          </div>
          <h2 className="font-head text-3xl font-extrabold mb-3">Nous vous accompagnons de A à Z</h2>
          <p className="text-white/70 max-w-2xl mb-10">
            Quatre parcours d'accompagnement personnalisés, avec suivi du dossier étape par étape jusqu'à votre départ.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VISA_SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.key}
                  to="/visa"
                  data-testid={`visa-service-${s.key}`}
                  className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-6 transition-colors card-hover"
                >
                  <span className="h-12 w-12 rounded-xl bg-accent/20 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-accent" />
                  </span>
                  <h3 className="font-head font-bold text-lg mb-1">{s.title}</h3>
                  <p className="text-sm text-white/70">{s.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-br from-accent to-amber-500 rounded-3xl p-8 sm:p-12 text-center text-accent-foreground">
          <Plane className="h-10 w-10 mx-auto mb-4" />
          <h2 className="font-head text-3xl font-extrabold mb-3">Une question ? Parlons-en</h2>
          <p className="max-w-xl mx-auto mb-7 font-medium">
            Notre équipe est disponible sur WhatsApp pour vous conseiller sur vos séjours et vos démarches de visa.
          </p>
          {whatsapp && (
            <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" data-testid="home-whatsapp-btn">
              <Button className="rounded-full h-12 px-8 bg-primary text-white hover:bg-primary/90 text-base font-bold">
                <MessageCircle className="mr-2 h-5 w-5" /> Nous écrire sur WhatsApp
              </Button>
            </a>
          )}
        </div>
      </section>
    </div>
  );
}
